import { createDemGrid, gridCoverage, int16FromLittleEndian, parseDemMetadata, sampleElevation, type DemGrid, type DemMetadata, type ElevationSample, type Interpolation } from './demGrid';

/** Static, versioned assets served by the app itself (precached by the service worker for offline use). */
export const DEM_METADATA_URL = '/elevation/cop30-casur.v2.json';
export const DEM_DATA_URL = '/elevation/cop30-casur.v2.bin';
export const DEM_SOURCE_LABEL = 'GLO-30';

export type DemStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface DemSourceDeps {
  readonly fetch: (url: string) => Promise<Response>;
  /** gzip → bytes. Defaults to the browser's DecompressionStream. */
  readonly gunzip: (bytes: Uint8Array) => Promise<Uint8Array>;
  /** SHA-256 hex digest, or null when WebCrypto is unavailable (insecure context). */
  readonly sha256: (bytes: Uint8Array) => Promise<string | null>;
}

const browserGunzip = async (bytes: Uint8Array): Promise<Uint8Array> => {
  if (typeof DecompressionStream === 'undefined') throw new Error('Este navegador no puede descomprimir el modelo de elevación.');
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream('gzip'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
};

const browserSha256 = async (bytes: Uint8Array): Promise<string | null> => {
  const subtle = globalThis.crypto?.subtle; if (!subtle) return null;
  const digest = new Uint8Array(await subtle.digest('SHA-256', bytes as BufferSource));
  return [...digest].map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

const defaultDeps = (): DemSourceDeps => ({ fetch: (url) => fetch(url), gunzip: browserGunzip, sha256: browserSha256 });

const isGzip = (bytes: Uint8Array): boolean => bytes.length > 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;

/**
 * Loads the DEM clip once (two static files, no per-query network) and answers point queries from memory.
 * A file that does not match its declared size/hash is rejected: a wrong height is worse than "Sin dato".
 */
export class DemSource {
  private state: DemStatus = 'idle';
  private grid: DemGrid | null = null;
  private failure: string | null = null;
  private pending: Promise<DemGrid | null> | null = null;
  private readonly listeners = new Set<(status: DemStatus) => void>();

  constructor(private readonly deps: DemSourceDeps = defaultDeps(), private readonly urls = { metadata: DEM_METADATA_URL, data: DEM_DATA_URL }) {}

  get status(): DemStatus { return this.state; }
  get error(): string | null { return this.failure; }
  get metadata(): DemMetadata | null { return this.grid?.metadata ?? null; }
  get coverage(): readonly [number, number, number, number] | null { return this.grid ? gridCoverage(this.grid) : null; }

  onChange(listener: (status: DemStatus) => void): () => void { this.listeners.add(listener); return () => this.listeners.delete(listener); }

  /** Starts (or joins) the single load. Never rejects: failures end in status `error`. */
  load(): Promise<DemGrid | null> {
    if (this.grid) return Promise.resolve(this.grid);
    if (this.pending) return this.pending;
    this.setStatus('loading');
    this.pending = this.read().then((grid) => { this.grid = grid; this.failure = null; this.setStatus('ready'); return grid; }, (error: unknown) => {
      this.failure = error instanceof Error ? error.message : 'No se pudo cargar el modelo de elevación.';
      this.pending = null; this.setStatus('error'); return null;
    });
    return this.pending;
  }

  /** Synchronous query; `unavailable` until the grid is in memory. */
  sample(lon: number, lat: number, interpolation: Interpolation = 'bilinear'): ElevationSample | { readonly status: 'unavailable' } {
    return this.grid ? sampleElevation(this.grid, lon, lat, interpolation) : { status: 'unavailable' };
  }

  private async read(): Promise<DemGrid> {
    const metaResponse = await this.deps.fetch(this.urls.metadata);
    if (!metaResponse.ok) throw new Error(`Modelo de elevación no disponible (${metaResponse.status}).`);
    const metadata = parseDemMetadata(await metaResponse.json());
    const dataResponse = await this.deps.fetch(this.urls.data);
    if (!dataResponse.ok) throw new Error(`Datos de elevación no disponibles (${dataResponse.status}).`);
    let bytes = new Uint8Array(await dataResponse.arrayBuffer());
    // A server may already have removed the gzip layer (Content-Encoding); only inflate real gzip bytes.
    if (isGzip(bytes)) bytes = await this.deps.gunzip(bytes);
    if (bytes.byteLength !== metadata.files.raw.bytes) throw new Error('El archivo de elevación no coincide con sus metadatos (tamaño).');
    const digest = await this.deps.sha256(bytes);
    if (digest !== null && digest !== metadata.files.raw.sha256) throw new Error('El archivo de elevación no coincide con sus metadatos (SHA-256).');
    return createDemGrid(metadata, int16FromLittleEndian(bytes));
  }

  private setStatus(status: DemStatus): void {
    this.state = status;
    this.listeners.forEach((listener) => listener(status));
  }
}
